import {AxiosResponse} from 'axios';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import {useTranslation} from 'react-i18next';

import AddressModal from '../../components/common/\bAddressModal';
import BrandModal from '../../components/common/BrandModal';
import CategoryModal from '../../components/common/CategoryModal';
import TempAlert from '../../components/common/TempAlert';
import TempModal from '../../components/common/TempModal';
import Picture from '../../components/community/editor/Picture';
import useCanvas from '../../hooks/useCanvas';
import useS3 from '../../hooks/useS3';
import ContentsHeader from '../../layouts/ContentsHeader';
import {
  deleteReviewTemp,
  getCommunityContents,
  getReviewTemp,
  saveReviewTemp,
} from '../../services/community';
import {uploadImage} from '../../services/user';
import {
  AddressList,
  CommunityCategory,
  EditableCommunity,
  isOpenCommunityEditCancelAlert,
  isOpenCommunityLoginModal,
  isOpenTempAlert,
  isOpenToast,
  tempAlertType,
  ToastMessage,
} from '../../store';
import {user, UserInfo} from '../../store/user';
import CommunityPreview from './Preview';
import Layout from '../../layouts';

interface TempItem {
  author: number;
  contents: CommunityContentReq;
  created: string;
  id: number;
}
interface CategoryItemType {
  txt: Category;
  param: CategoryParam;
}

export default function CommunityContentsEditor() {
  const uploaderRef = useRef(null);
  const s3 = useS3();
  const navigator = useNavigate();
  const location = useLocation();
  const {translateImg} = useCanvas();
  const [imageList, setImageList] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useRecoilState(CommunityCategory);
  const [isOpenCategory, setIsOpenCategory] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandInfo>();
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isOpenBrand, setIsOpenBrand] = useState(false);
  const [isOpenAddress, setIsOpenAddress] = useState(false);
  const [addressIds, setAddressIds] = useState([0, 0]);
  const [tempItem, setTempItem] = useState<TempItem>();
  const [mode, setMode] = useState<'editor' | 'preview'>('editor');
  const [pageType, setPageType] = useState<'add' | 'edit'>('add');
  const setIsOpenToast = useSetRecoilState(isOpenToast);
  const setToastMessage = useSetRecoilState(ToastMessage);
  const setEditableCommunity = useSetRecoilState(EditableCommunity);
  const setIsOpenTempAlert = useSetRecoilState(isOpenTempAlert);
  const setOpenCommunityLogin = useSetRecoilState(isOpenCommunityLoginModal);
  const resetEditableCommunity = useResetRecoilState(EditableCommunity);
  const editableCommunity = useRecoilValue(EditableCommunity);
  const userInfo = useRecoilValue(UserInfo);
  const accessToken = useRecoilValue(user);
  const parentAddress = useRecoilValue(AddressList);
  const [_tempAlertType, setTempAlertType] = useRecoilState(tempAlertType);
  const [enableFileUpload, setEnableFileUpload] = useState(true);
  const setOpenCommunityEditCancelAlert = useSetRecoilState(
    isOpenCommunityEditCancelAlert,
  );
  const {t} = useTranslation(['views'], {keyPrefix: 'community.Editor'});
  const categories: CategoryItemType[] = [
    {
      txt: 'Special',
      param: 'special',
    },
    {
      txt: 'Family',
      param: 'family',
    },
    {
      txt: 'Refresh',
      param: 'refresh',
    },
    {
      txt: 'Eco-friendly',
      param: 'eco_friendly',
    },
    {
      txt: 'Pet-friendly',
      param: 'pet_friendly',
    },
    {
      txt: 'Healthy',
      param: 'healthy',
    },
    {
      txt: 'Drinking',
      param: 'drinking',
    },
    {
      txt: 'Daily',
      param: 'daily',
    },
  ];
  const hasAuth = useMemo(() => {
    return (
      !!userInfo &&
      (userInfo.userType === 'creator' || userInfo.userType === 'curator')
    );
  }, [userInfo]);
  const initState = () => {
    setImageList([]);
    setSelectedCategory(undefined);
    setSelectedBrand(undefined);
    setSelectedAddress('');
    setTitle('');
    setDescription('');
    setAddressIds([0, 0]);
    setTempItem(undefined);
    setMode('editor');
    setTempAlertType('save');
    resetEditableCommunity();
  };

  const isAllSet = () => {
    return (
      selectedAddress.length > 0 &&
      selectedBrand &&
      selectedBrand.title.length > 0 &&
      !!selectedCategory &&
      imageList.length > 0 &&
      title.length > 0 &&
      description.length > 50
    );
  };

  const isAbleSave = () => {
    return (
      selectedAddress.length > 0 ||
      selectedBrand ||
      !!selectedCategory ||
      imageList.length > 0 ||
      title.length > 0 ||
      description.length > 0
    );
  };

  const upload = async (file: File) => {
    return new Promise<string>((resolve, reject) => {
      translateImg(file, 'community', async (makedFile: File) => {
        try {
          const presignedRes = await uploadImage(file.name, 'review');
          await s3.uploadImage(
            makedFile,
            makedFile.name,
            `${presignedRes.data[0].url}`,
            presignedRes.data[0].fields,
          );
          resolve(
            `${presignedRes.data[0].url}${presignedRes.data[0].fields.key}`,
          );
        } catch (e) {
          reject(e);
        }
      });
    });
  };
  const setAddress = (address: string, ids: number[]) => {
    setSelectedAddress(address);
    setAddressIds(ids);
  };
  const callFileUploader = () => {
    if (enableFileUpload) {
      (uploaderRef.current as any).click();
    }
  };
  const handleTitle = ($event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTitle($event.currentTarget.value);
  };
  const handleDescription = (
    $event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setDescription($event.currentTarget.value);
  };
  const getTempItems = async () => {
    const res: AxiosResponse<TempItem[]> = await getReviewTemp(accessToken);
    setTempItem(res.data.length > 0 ? res.data[0] : undefined);
  };
  const saveTemp = async () => {
    const param: CommunityContentReq = {
      images: imageList.map((o, i) => {
        return {
          image: o,
          priority: i,
        };
      }),
      title,
      content: description,
      category: selectedCategory ? selectedCategory.param : '',
      address1: addressIds[0],
      address2: addressIds[1],
      brands: selectedBrand ? selectedBrand.id : -1,
      brandItem: selectedBrand,
      addresStr: selectedAddress,
    };
    await saveReviewTemp(param, accessToken);
    setToastMessage(t('saveToast'));
    setIsOpenToast(true);
    setIsOpenTempAlert(false);
  };
  const removeTemp = async () => {
    await deleteReviewTemp(accessToken);
    getTempItems();
    setIsOpenTempAlert(false);
  };
  const changeImage = async ($event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const imgs = $event.currentTarget.files as FileList;
      setEnableFileUpload(false);
      if ((imgs as FileList).length > 0) {
        // const requests = [];
        const max = imgs.length > 10 ? 10 : imgs.length;
        const paths = [];
        for (let i = 0; i < max; i++) {
          // requests.push(upload.apply(null, [imgs[i]]));
          const path = await upload(imgs[i]);
          paths.push(path);
        }
        setImageList([...imageList, ...paths]);
        setEnableFileUpload(true);
        // Promise.all(requests).then(paths => {
        //   setImageList([...imageList, ...paths]);
        //   setEnableFileUpload(true);
        // });
      }
    } catch (e) {
      setToastMessage(t('imgErrToast'));
      setIsOpenToast(true);
      setEnableFileUpload(true);
    }
  };
  const removeImage = (position: number) => {
    const imgs = [...imageList];
    imgs.splice(position, 1);
    setImageList(imgs);
  };
  const makeDefault = () => {
    setTitle(editableCommunity.title);
    setDescription(editableCommunity.content);
    const categoryObj = categories.find(
      c => c.param === editableCommunity.category,
    );
    if (categoryObj) {
      setSelectedCategory(categoryObj);
    }
    setImageList(editableCommunity.images.map(img => img.image));
    setSelectedBrand(
      editableCommunity.brandItem ? editableCommunity.brandItem : undefined,
    );
    setSelectedAddress(
      editableCommunity.addresStr
        ? editableCommunity.addresStr != 'null null'
          ? editableCommunity.addresStr
          : '온라인 스토어'
        : '',
    );
    if (editableCommunity.addresStr && editableCommunity.addresStr.length > 0) {
      const str = (editableCommunity.addresStr as string).split(' ');
      const parent = parentAddress.find(o => o.address === str[0]);
      const addressArr = [
        parent?.id as number,
        parent?.child?.find(c => c.address === str[1])?.id as number,
      ];
      setAddressIds(addressArr);
    }
  };
  const getCommunity = async (id: number) => {
    const res: AxiosResponse<Community> = await getCommunityContents(
      Number(id),
      accessToken,
    );
    setPageType('edit');
    moveToEdit(res.data);
  };
  const moveToEdit = async (contents: Community) => {
    const param: CommunityContentReq = {
      title: contents?.title as string,
      content: contents?.content as string,
      brands: contents?.brands.id as number,
      brandItem: contents?.brands,
      addresStr: `${contents?.address1} ${contents?.address2}`,
      address1: -1,
      address2: -1,
      category: contents?.category as Category,
      images: contents?.images as ImageObj[],
    };
    setEditableCommunity(param);
  };
  const openTempAlert = async () => {
    await setTempAlertType('save');
    setIsOpenTempAlert(true);
  };
  const back = () => {
    if (pageType === 'edit') {
      return setOpenCommunityEditCancelAlert(true);
    }
    if (
      !!(location.state as any) &&
      (location.state as any).history &&
      (location.state as any).history === 'my-content'
    ) {
      return navigator('/my-contents');
    }
    if (
      !!(location.state as any) &&
      (location.state as any).history &&
      (location.state as any).history === 'my-page'
    ) {
      return navigator('/user');
    }
    if (isAbleSave()) {
      openTempAlert();
    } else {
      navigator('/community');
    }
  };
  const contentsHeaderOption = useMemo(() => {
    return pageType === 'edit' ? 'none' : tempItem ? 'write-active' : 'write';
  }, [pageType, tempItem]);
  useEffect(() => {
    const path = window.location.pathname.split('/');
    const id = path[path.length - 1];
    const isEdit = path[path.length - 2] === 'editor';
    if (!userInfo) {
      setOpenCommunityLogin(true);
    }
    if (isEdit) {
      setPageType('edit');
    }
    if (editableCommunity) {
      makeDefault();
    } else {
      id !== 'add' && getCommunity(Number(id));
    }
    getTempItems();
  }, [editableCommunity]);
  useEffect(() => {
    scrollTo(0, 0);
  }, [mode]);
  useEffect(() => {
    initState();
  }, []);

  return (
    <Layout.Main>
      <div className="community-edit-contents">
        {hasAuth && (
          <div>
            <div style={{display: mode === 'editor' ? 'block' : 'none'}}>
              <ContentsHeader
                txt={
                  pageType === 'edit' ? t('header_edit') : t('header_create')
                }
                isOption={contentsHeaderOption}
                back={() => back()}
              />
              <div className="add-img-wrapper contents-body">
                <Picture
                  type="add"
                  cnt={imageList.length}
                  callFileUploader={callFileUploader}
                  isEdit={pageType === 'edit'}
                />
                {imageList.map((img, idx) => {
                  return (
                    <div
                      key={`add-img-${img}-${idx}`}
                      className="uploaded-image"
                      style={{
                        background: `url(${img}) no-repeat center / cover`,
                      }}>
                      <span
                        className="delete-btn"
                        onClick={() => removeImage(idx)}>
                        <span className="icon white-close-icon">&nbsp;</span>
                      </span>
                      <div
                        className="cover"
                        style={{
                          display: pageType === 'edit' ? 'block' : 'none',
                        }}></div>
                    </div>
                  );
                })}
              </div>
              <div className="community-title">
                <textarea
                  placeholder={t('title_placeholder')}
                  onChange={handleTitle}
                  value={title}
                  maxLength={20}
                  cols={2}
                />
              </div>
              <div className="contents-editor">
                <textarea
                  placeholder={`${t('content_placeholder1')}
${t('content_placeholder2')}`}
                  value={description}
                  onChange={handleDescription}
                  maxLength={2000}
                />
              </div>
              <div className="contents-limit">
                <span className="txt">({t('content_limit')})</span>
                <span
                  className={`desc-cnt ${
                    description.length > 0 ? 'active' : ''
                  }`}>
                  {description.length}
                </span>
                /2000
              </div>
              <div className="contents-warning">
                <strong>{t('content_warning1')}</strong>
                {t('content_warning2')}
                <strong>{t('content_warning3')}</strong>
                {t('content_warning4')}
                <br />
                {t('content_warning5')}
              </div>
              <div className="contents-options">
                <div
                  className="option-item"
                  onClick={() => setIsOpenCategory(true)}>
                  <div className="name">
                    {t('option_category')}
                    <span className="mendatory">*</span>
                  </div>
                  <div className="arrow-wrapper">
                    <div className="selection">{selectedCategory?.txt}</div>
                    <span className="icon arrow-right-thick-icon">&nbsp;</span>
                  </div>
                </div>
                <div
                  className="option-item"
                  onClick={() => setIsOpenBrand(true)}>
                  <div className="name">
                    {t('option_brand_name')}
                    <span className="mendatory">*</span>
                  </div>
                  <div className="arrow-wrapper">
                    <div className="selection">{selectedBrand?.title}</div>
                    <span className="icon arrow-right-thick-icon">&nbsp;</span>
                  </div>
                </div>
                <div
                  className="option-item"
                  onClick={() => setIsOpenAddress(true)}>
                  <div className="name">
                    {t('option_address')}
                    <span className="mendatory">*</span>
                  </div>
                  <div className="arrow-wrapper">
                    <div className="selection">{selectedAddress}</div>
                    <span className="icon arrow-right-thick-icon">&nbsp;</span>
                  </div>
                </div>
              </div>
              <div className="contents-btn-wrapper">
                <button
                  className={`btn next ${isAllSet() ? '' : 'disabled'}`}
                  disabled={!isAllSet()}
                  onClick={() => setMode('preview')}>
                  {t('btn_next')}
                </button>
                {pageType === 'add' ? (
                  <button
                    className="btn temp-save"
                    onClick={() => openTempAlert()}>
                    {t('btn_temp')}
                  </button>
                ) : (
                  <button
                    className="btn temp-save"
                    onClick={() => setOpenCommunityEditCancelAlert(true)}>
                    {t('btn_cancel')}
                  </button>
                )}
              </div>
              <CategoryModal
                isOpen={isOpenCategory}
                togglePopup={(is: boolean) => setIsOpenCategory(is)}
              />
              <BrandModal
                select={(sc: BrandInfo) => setSelectedBrand(sc)}
                isOpen={isOpenBrand}
                togglePopup={(is: boolean) => setIsOpenBrand(is)}
              />
              <AddressModal
                selectedAddress={selectedAddress}
                select={(sc: string, ids: number[]) => setAddress(sc, ids)}
                isOpen={isOpenAddress}
                togglePopup={(is: boolean) => setIsOpenAddress(is)}
              />
              <input
                ref={uploaderRef}
                type="file"
                accept="image/*,.heic,.heif,capture=camera"
                style={{visibility: 'hidden'}}
                onChange={($event: React.ChangeEvent<HTMLInputElement>) =>
                  changeImage($event)
                }
                multiple
              />
            </div>
            <CommunityPreview
              mode={mode}
              setMode={(mode: 'preview' | 'editor') => setMode(mode)}
              imageList={imageList}
              category={selectedCategory as CategoryItem}
              title={title}
              contents={description}
              address={selectedAddress}
              author={userInfo}
              brand={selectedBrand as BrandInfo}
              addressIds={addressIds}
              isTemp={!!editableCommunity && pageType === 'add'}
              removeTemp={removeTemp}
            />
            <TempAlert
              type={_tempAlertType}
              save={saveTemp}
              remove={removeTemp}
              tempItem={tempItem}
            />
            <TempModal tempItem={tempItem} />
          </div>
        )}
      </div>
    </Layout.Main>
  );
}
